import { c as _c } from "react/compiler-runtime";
import { Autocomplete, FormLabel, Stack, TextField, Typography, FormControl, type SxProps } from '@mui/material';
import { useState } from 'react';
import type React from 'react';
import { HelpIcon } from '@/components/shared/helpIcon';
import type { FieldValidation } from './validation/fieldValidation';
import { InputErrorIcon } from './validation/inputErrorIcon';
interface FormTextFieldBaseProps {
  size?: 'small' | 'medium';
  testId?: string;
  title: React.ReactNode;
  helpText?: React.ReactNode;
  alwaysShowHelp?: boolean;
  dataTestId?: string;
  validation?: FieldValidation;
  placeholder: string;
  value: string;
  sx?: SxProps;
  disabled?: boolean;
  autoFocus?: boolean;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  type?: React.HTMLInputTypeAttribute;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  endAdornment?: React.ReactNode;
  showValidationError?: boolean;
}

// Free text stays possible with suggestions, so the two modes differ only in
// how the new value is reported: a picked suggestion has no input event.
type FormTextFieldProps = FormTextFieldBaseProps & ({
  suggestions?: undefined;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onValueChange?: undefined;
} | {
  suggestions: string[];
  onChange?: undefined;
  onValueChange: (value: string) => void;
});
export const FormTextField = t0 => {
  const $ = _c(44);
  const {
    size: t1,
    testId,
    title,
    helpText,
    alwaysShowHelp: t2,
    dataTestId,
    validation,
    placeholder,
    value,
    onChange,
    onValueChange,
    suggestions,
    sx,
    disabled: t3,
    autoFocus: t4,
    multiline: t5,
    rows: t6,
    maxLength,
    type,
    inputProps,
    endAdornment,
    showValidationError: t7
  } = t0;
  const size = t1 === undefined ? "medium" : t1;
  const alwaysShowHelp = t2 === undefined ? false : t2;
  const disabled = t3 === undefined ? false : t3;
  const autoFocus = t4 === undefined ? false : t4;
  const multiline = t5 === undefined ? false : t5;
  const rows = t6 === undefined ? 1 : t6;
  const showValidationError = t7 === undefined ? false : t7;
  const [touched, setTouched] = useState(false);
  let t8;
  if ($[0] !== showValidationError || $[1] !== touched || $[2] !== validation || $[3] !== value) {
    t8 = (touched || showValidationError) && (validation ? !validation.validateInput(value) : false);
    $[0] = showValidationError;
    $[1] = touched;
    $[2] = validation;
    $[3] = value;
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const hasError = t8;
  let t9;
  if ($[5] !== endAdornment || $[6] !== hasError || $[7] !== validation || $[8] !== value) {
    t9 = hasError ? <InputErrorIcon errorMessage={validation?.errorMessage(value)} /> : endAdornment;
    $[5] = endAdornment;
    $[6] = hasError;
    $[7] = validation;
    $[8] = value;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const errorAdornment = t9;
  let t10;
  if ($[10] !== dataTestId) {
    t10 = dataTestId ? {
      "data-testid": dataTestId
    } : {};
    $[10] = dataTestId;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== maxLength) {
    t11 = maxLength !== undefined ? {
      maxLength
    } : {};
    $[12] = maxLength;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== inputProps || $[15] !== t10 || $[16] !== t11) {
    t12 = {
      ...inputProps,
      ...t10,
      ...t11
    };
    $[14] = inputProps;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const htmlInputProps = t12;
  let t13;
  if ($[18] !== sx) {
    t13 = {
      width: "100%",
      ...sx
    };
    $[18] = sx;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== alwaysShowHelp || $[21] !== helpText || $[22] !== title) {
    t14 = title && <FormLabel><Stack direction="row" sx={{
        alignItems: "center",
        justifyContent: "space-between"
      }}><Typography variant="subtitle1">{title}</Typography>{helpText && <HelpIcon title={helpText} alwaysVisible={alwaysShowHelp} className="text-(--mui-palette-text-secondary)" />}</Stack></FormLabel>;
    $[20] = alwaysShowHelp;
    $[21] = helpText;
    $[22] = title;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== autoFocus || $[25] !== disabled || $[26] !== errorAdornment || $[27] !== hasError || $[28] !== htmlInputProps || $[29] !== multiline || $[30] !== onChange || $[31] !== onValueChange || $[32] !== placeholder || $[33] !== rows || $[34] !== size || $[35] !== suggestions || $[36] !== testId || $[37] !== type || $[38] !== value) {
    t15 = suggestions ? <Autocomplete freeSolo={true} size={size} disabled={disabled} value={value} options={suggestions} onBlur={() => setTouched(true)} onInputChange={(_, next) => onValueChange(next)} renderInput={params => <TextField {...params} data-testid={testId} error={hasError} placeholder={placeholder} type={type} slotProps={{
      ...params.slotProps,
      htmlInput: {
        ...params.slotProps.htmlInput,
        ...htmlInputProps
      },
      input: {
        ...params.slotProps.input,
        endAdornment: <>{errorAdornment}{params.slotProps.input.endAdornment}</>
      }
    }} />} /> : <TextField multiline={multiline} rows={rows} size={size} autoFocus={autoFocus} data-testid={testId} onBlur={() => setTouched(true)} error={hasError} sx={{
      "& .MuiInputBase-root": {
        paddingRight: hasError ? "4px" : "24px"
      },
      ...(multiline && {
        "& .MuiOutlinedInput-input": {
          paddingLeft: "0px"
        }
      })
    }} slotProps={{
      htmlInput: htmlInputProps,
      input: {
        endAdornment: errorAdornment
      }
    }} type={type} placeholder={placeholder} disabled={disabled} value={value} onChange={onChange} />;
    $[24] = autoFocus;
    $[25] = disabled;
    $[26] = errorAdornment;
    $[27] = hasError;
    $[28] = htmlInputProps;
    $[29] = multiline;
    $[30] = onChange;
    $[31] = onValueChange;
    $[32] = placeholder;
    $[33] = rows;
    $[34] = size;
    $[35] = suggestions;
    $[36] = testId;
    $[37] = type;
    $[38] = value;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== t13 || $[41] !== t14 || $[42] !== t15) {
    t16 = <FormControl className="group/help" sx={t13}>{t14}{t15}</FormControl>;
    $[40] = t13;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  return t16;
};